import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchFlow,
  updateFlow,
  updatenodeFlow,
  fetchAllFlow,
  createFlow,
  deleteFlow,
} from './flowAPI';


export const getFlow = createAsyncThunk(
  'flow/getFlow',
  async (uuid, thunkAPI) => {
    try {
      const data = await fetchFlow(uuid);
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getAllFlows = createAsyncThunk(
  'flow/getAllFlows',
  async (_, thunkAPI) => {
    try {
      const data = await fetchAllFlow();
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addFlow = createAsyncThunk(
  'flow/addFlow',
  async (flowData, thunkAPI) => {
    try {
      const data = await createFlow(flowData);
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const editFlow = createAsyncThunk(
  'flow/editFlow',
  async ({ uuid, data }, thunkAPI) => {
    try {
      const { nodes, edges } = data;
      const updated = await updateFlow(uuid, { nodes, edges });
      return updated;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const editnodeFlow = createAsyncThunk(
  'flow/editnodeFlow',
  async ({ uuid, data }, thunkAPI) => {
    try {
      const updated = await updatenodeFlow(uuid, data);
      return updated;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const removeFlow = createAsyncThunk(
  'flow/removeFlow',
  async (uuid, thunkAPI) => {
    try {
      const data = await deleteFlow(uuid);
      return { uuid, data };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);


const flowSlice = createSlice({
  name: 'flow',
  initialState: {
    flows: [],
    selectedFlow: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearSelectedFlow(state) {
      state.selectedFlow = null;
      state.error = null;
    },
    clearError(state) {
      state.error = null;
    },
    updateSelectedFlow(state, action) {
      if (state.selectedFlow && state.selectedFlow.uuid === action.payload.uuid) {
        state.selectedFlow = {
          ...state.selectedFlow,
          ...action.payload,
        };
      }

      const idx = state.flows.findIndex((f) => f.uuid === action.payload.uuid);
      if (idx !== -1) {
        state.flows[idx] = {
          ...state.flows[idx],
          ...action.payload,
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // getFlow
      .addCase(getFlow.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getFlow.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedFlow = action.payload;
      })
      .addCase(getFlow.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // getAllFlows
      .addCase(getAllFlows.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllFlows.fulfilled, (state, action) => {
        state.loading = false;
        state.flows = action.payload;
      })
      .addCase(getAllFlows.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // addFlow
      .addCase(addFlow.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addFlow.fulfilled, (state, action) => {
        state.loading = false;
        state.flows.push(action.payload);
      })
      .addCase(addFlow.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // editFlow
      .addCase(editFlow.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editFlow.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.flows.findIndex((f) => f.uuid === action.payload.uuid);
        if (idx !== -1) state.flows[idx] = action.payload;
        if (state.selectedFlow?.uuid === action.payload.uuid) {
          state.selectedFlow = action.payload;
        }
      })
      .addCase(editFlow.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // editnodeFlow
      .addCase(editnodeFlow.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editnodeFlow.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.flows.findIndex((f) => f.uuid === action.payload.uuid);
        if (idx !== -1) {
          state.flows[idx] = {
            ...state.flows[idx],
            ...action.payload,
            nodes: action.payload.nodes ?? state.flows[idx].nodes,
            edges: action.payload.edges ?? state.flows[idx].edges,
          };
        }
        if (state.selectedFlow?.uuid === action.payload.uuid) {
          state.selectedFlow = {
            ...state.selectedFlow,
            ...action.payload,
            nodes: action.payload.nodes ?? state.selectedFlow.nodes,
            edges: action.payload.edges ?? state.selectedFlow.edges,
          };
        }
      })
      .addCase(editnodeFlow.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // removeFlow
      .addCase(removeFlow.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeFlow.fulfilled, (state, action) => {
        state.loading = false;
        state.flows = state.flows.filter((f) => f.uuid !== action.payload.uuid);
        if (state.selectedFlow?.uuid === action.payload.uuid) {
          state.selectedFlow = null;
        }
      })
      .addCase(removeFlow.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearSelectedFlow, clearError, updateSelectedFlow } = flowSlice.actions;

export default flowSlice.reducer;
